import _ from 'lodash';
import moment from 'moment';
import Raven from 'raven-js';
import { CardDataEncrypter } from '@toasttab/ccbrowser';

/**
 * Holds the information needed to place an order
 */

export function OrderInfoService(
    $http,
    $q,
    $state,
    Analytics,
    AuthenticationService,
    APPROVAL_RULE_ERROR,
    EMPTY_CART_PLACE_ORDER_ERROR,
    INVALID_DATE_ERROR,
    LOCAL_STORAGE,
    OUT_OF_STOCK_ERROR,
    ToastApiService,
    availabilitiesFactory,
    blockingCalloutService,
    cartService,
    googleAnalyticsTrackingService,
    localStorageService,
    paymentService,
    performanceService
) {
    'ngInject';
    let cardDataEncrypter = null;
    let startPerfTiming;
    let cartData = {},
        paymentData = {},
        orderComplete = $q.defer(),
        savedPaymentKeys = ['method', 'tip'];

    function activate() {
        try {
            cardDataEncrypter = new CardDataEncrypter();
        } catch (e) {
            Raven.captureMessage('CardDataEncrypter failed to initialize.', {
                level: 'info'
            });
        }

        startPerfTiming = performanceService.timingCategory('OrderInfoService');

        // Obtain a reference to the cart
        cartService.getCart()
            .then(function() {
                cartData = cartService.getCartData();
            });
    }

    var _setDatumGeneric = function(dataType, key, value) {
        if (typeof key === 'string' && (typeof value === 'string' || value === undefined)) {
            var localStorageData = localStorageService.get(dataType) || {};
            localStorageData[key] = value;
            localStorageService.set(dataType, localStorageData);
        }
    };

    var _setDataGeneric = function(dataType, object) {
        if (typeof object === 'object') {
            var localStorageData = localStorageService.get(dataType) || {};
            let keys = Object.keys(localStorageData);
            for (let key of keys) {
                object[key] = object[key] || null;
            }
            localStorageService.set(dataType, object);
        }
    };

    var _getGeneric = function(dataType) {
        if (arguments.length === 1 && typeof dataType === 'string') {
            if (dataType === LOCAL_STORAGE.PAYMENT) {
                return paymentData;
            } else {
                return localStorageService.get(dataType) || {};
            }
        }
    };

    // Customer Info Setters / Getter
    var setCustomerDatum = function(key, value) {
        _setDatumGeneric(LOCAL_STORAGE.CUSTOMER, key, value);
    };

    var setCustomerData = function(object) {
        _setDataGeneric(LOCAL_STORAGE.CUSTOMER, object);
    };

    var getCustomer = function() {
        return _getGeneric(LOCAL_STORAGE.CUSTOMER);
    };

    // Delivery Info Setters / Getter
    var setDeliveryInfoDatum = function(key, value) {
        _setDatumGeneric(LOCAL_STORAGE.DELIVERY_INFO, key, value);
    };

    var setDeliveryInfoData = function(object) {
        if(!object.guid) object.guid = '';
        if(!object.dateTime) object.dateTime = moment.parseZone(availabilitiesFactory.getInitialDateTime()).toISOString();
        _setDataGeneric(LOCAL_STORAGE.DELIVERY_INFO, object);
    };

    var getDeliveryInfo = function() {
        const deliveryInfo = _getGeneric(LOCAL_STORAGE.DELIVERY_INFO);
        if(!deliveryInfo.dateTime) return {};
        const setDate = moment.parseZone(deliveryInfo.dateTime);
        const currentDate = moment.parseZone(availabilitiesFactory.getInitialDateTime());
        if(setDate.add(30, 'days').isAfter(currentDate)) return deliveryInfo;
        return {};
    };

    var getOrderData = function() {
        return orderComplete.promise;
    };

    // Payment Setters / Getter

    var clearTip = function() {
        let newPaymentData = getPayment();
        newPaymentData.tip = '0';
        _setPaymentData(newPaymentData);
        paymentService.clearTip();
    };

    var setPaymentData = function({ method, cardNumber, expiryMonth, expiryYear, securityCode, postalCode, tip = null, saveCard, guid, maskedPan }) {
        if (method !== 'CREDIT') {
            tip = null;
            saveCard = false;
        }
        let newPaymentData = { method, cardNumber, expiryMonth, expiryYear, securityCode, postalCode, tip, saveCard, guid, maskedPan };
        return _addEncryptedCard(newPaymentData).then(newPaymentData => _setPaymentData(newPaymentData));
    };

    var _setPaymentData = function(newPaymentData) {
        paymentData = newPaymentData;
        savedPaymentKeys.map(key => _setDatumGeneric(LOCAL_STORAGE.PAYMENT, key, paymentData[key]));
    };

    var _addEncryptedCard = function(newPaymentData) {
        if (!newPaymentData.cardNumber) {
            return $q.resolve(newPaymentData);
        }
        if (!cardDataEncrypter) {
            return $q.reject('This browser version cannot process your order. Please update or switch browsers.');
        }

        const card = {
            cardNumber: newPaymentData.cardNumber,
            zipCode: newPaymentData.postalCode,
            cvv: newPaymentData.securityCode,
            expMonth: newPaymentData.expiryMonth,
            expYear: newPaymentData.expiryYear,
        };
        let dfd = $q.defer();
        cardDataEncrypter.whenConstructed
            .then(() => cardDataEncrypter.encryptJsonCardData(card))
            .then(encryptedCard => {
                newPaymentData.encryptionKeyId = cardDataEncrypter.keyId;
                newPaymentData.encryptedCard = encryptedCard;
            })
            // this is for mimicking .finally() behavior for firefox
            // solution is based on https://stackoverflow.com/questions/35999072/what-is-the-equivalent-of-bluebird-promise-finally-in-native-es6-promises
            .catch(() => {})
            .then(() => dfd.resolve(newPaymentData));
        return dfd.promise;
    };

    var getPayment = function() {
        return _getGeneric(LOCAL_STORAGE.PAYMENT);
    };

    // Clear data
    function clearCCData() {
        var oldPayment = getPayment();
        var newPayment = {};

        if (!oldPayment.method) {
            oldPayment.method = localStorageService.get(LOCAL_STORAGE.PAYMENT).method;
        }

        for (var i = 0; i < savedPaymentKeys.length; i++) {
            var key = savedPaymentKeys[i];
            newPayment[key] = oldPayment[key];
        }
        setPaymentData(newPayment);
    }

    function clearOrderData() {
        orderComplete = $q.defer();
    }

    var clearAllData = function() {
        localStorageService.remove(LOCAL_STORAGE.CUSTOMER, LOCAL_STORAGE.DELIVERY_INFO, LOCAL_STORAGE.PAYMENT);
    };

    /**
     * Places the order on the server
     * @param {Function?} retryFunction function that will attempt to place the order again
     *  should the user wish to continue despite an error, such as an out of stock item.
     *  Generally the caller would pass in a reference to whatever function called placeOrder()
     *  in the first place so that it is assured the appropriate handlers are configured
     *  for the promise that placeOrder() returns.
     * @returns {Promise}
     */
    var placeOrder = function({ retryFunction } = {}) {
        if (cartData.cart) {
            const endPlaceOrderTiming = startPerfTiming('placeOrder');
            let placeOrderSuccessful = false;

            var fulfillmentDate = moment.parseZone(cartData.cart.fulfillmentDateTime);
            if (fulfillmentDate.isValid()) {
                fulfillmentDate = fulfillmentDate.format();
            } else {
                fulfillmentDate = 'null';
            }

            let customer = getCustomer();
            if (AuthenticationService.isAuthenticated()) {
                customer.guid = AuthenticationService.customerGuid;
            }

            const payment = getPayment();

            const finalOrder = {
                fulfillmentDate: fulfillmentDate,
                diningOptionBehavior: cartData.cart.diningOptionBehavior,
                customer: customer,
                deliveryInfo: getDeliveryInfo(),
                payment: {
                    encryptionKeyId: payment.encryptionKeyId,
                    encryptedCard: payment.encryptedCard,
                    guid: payment.guid,
                    method: payment.method,
                    saveCard: payment.saveCard,
                    tip: payment.tip
                },
                items: cartData.cart.items,
            };

            const sharedHttpArgs = {
                dataType: 'JSON',
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                }
            };
            const beforeAndPlaceOrderHttpArgs = {};
            let placeOrderErrorFunction = $q.reject;

            sharedHttpArgs.interceptorConfig = {
                blockingCallout: true
            };
            beforeAndPlaceOrderHttpArgs.interceptorConfig = {
                skipCallout: true
            };
            placeOrderErrorFunction = errResp => _placeOrderErrorHandler({ errResp, retryFunction });

            if (cartService.data.cart.check.totalAmount === 0
                && paymentService.data.selectedPayment.tip === 0
                && finalOrder.payment.saveCard
                && finalOrder.customer.guid !== undefined) {
                let element = angular.element(
                    `
                    <div class="zero-dollar-order-saved-cc-modal">
                        <h1>Yay!</h1>
                        <p>Your order is free, so we're not charging or saving your credit card info</p>
                    </div>
                    `
                );
                blockingCalloutService.show(element);
            }

            const beforeOrderFunction = () => {
                return $q.resolve({
                    data: {
                        cartGuid: cartData.cart.guid
                    }
                });
            };

            let placeOrderHttpArgs;
            const orderFunction = resp => {
                const orderUrl = _placeOrderUrl(resp.data.cartGuid, customer.guid);
                let placeOrderParams = {};

                if (finalOrder.payment.guid) {
                    placeOrderParams.cardGuid = finalOrder.payment.guid;
                } else if (finalOrder.payment.encryptedCard) {
                    placeOrderParams.encryptedCard = {
                        encryptionKeyId: finalOrder.payment.encryptionKeyId,
                        encryptedCardDataString: finalOrder.payment.encryptedCard,
                    };
                    placeOrderParams.saveCard = finalOrder.payment.saveCard;
                }

                placeOrderParams.customer = _.cloneDeep(finalOrder.customer);
                placeOrderParams.customer.guid = null;
                placeOrderParams.payment = {
                    type: finalOrder.payment.method,
                    tipAmount: finalOrder.payment.tip || 0,
                };
                placeOrderParams.deliveryInfo = finalOrder.deliveryInfo;

                placeOrderHttpArgs = Object.assign({
                    url: orderUrl,
                    data: placeOrderParams
                }, sharedHttpArgs, beforeAndPlaceOrderHttpArgs);
                return $http(placeOrderHttpArgs);
            };

            return beforeOrderFunction()
                .then(orderFunction, $q.reject)
                .then(orderResponse => {
                    placeOrderSuccessful = true;
                    _setOrderData(orderResponse.data);
                    _trackEcommerceEvent(orderResponse, cartData.cart);

                    const afterPlaceOrderHttpArgs = Object.assign({
                        url: 'afterorder',
                        data: JSON.stringify(Object.assign({
                            checkGuid: orderResponse.data.checkGuid || orderResponse.data.order.checks[0].guid,
                            customerGuid: customer.guid,
                        }, orderResponse.data))
                    }, sharedHttpArgs, {
                        interceptorConfig: {
                            skipCallout: true
                        }
                    });

                    let afterPlaceOrderErrorCount = 1;
                    let afterPlaceOrderErrorFunction;
                    const afterPlaceOrderFunction = () => $http(afterPlaceOrderHttpArgs)
                        .catch(afterPlaceOrderErrorFunction);

                    afterPlaceOrderErrorFunction = afterPlaceOrderErrResp => _afterPlaceOrderErrorHandler({
                        placeOrderHttpArgs,
                        afterPlaceOrderHttpArgs,
                        afterPlaceOrderErrResp,
                        afterPlaceOrderErrorCount: afterPlaceOrderErrorCount++,
                        retryFunction: afterPlaceOrderFunction
                    });

                    return afterPlaceOrderFunction();
                }, placeOrderErrorFunction)
                .finally(() => {
                    endPlaceOrderTiming({
                        analyticsLabel: placeOrderSuccessful ? 'success' : 'error',
                        analyticsIncludeNavigationStart: true
                    });
                });
        } else {
            return $q.reject();
        }
    };

    function _placeOrderErrorHandler({ errResp, retryFunction }) {
        const { errorMessage, error } = (errResp || {}).data || {};
        return _placeOrderErrorHandlerCalloutConfig({ errorMessage, error, retryFunction })
            .then(calloutConfig => {
                const res = blockingCalloutService.error(errorMessage, error, calloutConfig);
                return $q.reject(res);
            });
    }

    function _placeOrderErrorHandlerCalloutConfig({ errorMessage, error, retryFunction }) {
        let config = {};
        const goToMenuText = 'Go To Menu';
        const goToMenuFn = () => $state.go('order');

        if (OUT_OF_STOCK_ERROR === error) {
            cartService.clearCart();
            return cartService.getCart()
                .then(cart => {
                    const isCartEmpty = !(cart.items && cart.items.length);

                    if (retryFunction && !isCartEmpty) {
                        config.okText = 'Place Order';
                        config.okCallback = retryFunction;
                        config.cancelText = goToMenuText;
                        config.cancelCallback = goToMenuFn;
                        config.message = `${errorMessage} Do you want to return to the menu or continue placing your order?`;
                    } else {
                        if (!isCartEmpty) {
                            config.cancelText = true;
                        }

                        config.okText = goToMenuText;
                        config.okCallback = goToMenuFn;
                        config.message = isCartEmpty ? `${errorMessage} Your cart is now empty.` : `${errorMessage} Do you want to return to the menu?`;
                    }

                    return $q.resolve(config);
                });
        }
        else if (INVALID_DATE_ERROR === error) {
            config.cancelText = true;
            config.okText = goToMenuText;
            config.okCallback = goToMenuFn;
            config.message = `${errorMessage} Let's go back to your order and select a different time.`;
        }
        else if (EMPTY_CART_PLACE_ORDER_ERROR === error) {
            config.okText = goToMenuText;
            config.okCallback = goToMenuFn;
        }
        else if (APPROVAL_RULE_ERROR === error) {
            config.cancelText = true;
            config.okText = goToMenuText;
            config.okCallback = goToMenuFn;
        }

        return $q.resolve(config);
    }

    function _afterPlaceOrderErrorHandler({ placeOrderHttpArgs, afterPlaceOrderHttpArgs, afterPlaceOrderErrResp, afterPlaceOrderErrorCount, retryFunction }) {
        placeOrderHttpArgs = _.cloneDeep(placeOrderHttpArgs);

        if (_.hasIn(placeOrderHttpArgs, 'data.encryptedCard')) {
            placeOrderHttpArgs.data.encryptedCard = 'REDACTED';
        }

        googleAnalyticsTrackingService.trackAfterPlaceOrderErrorOccurred();
        Raven.captureMessage('placeOrder() succeeded but afterPlaceOrder() failed', {
            level: 'warning',
            extra: { placeOrderHttpArgs, afterPlaceOrderHttpArgs, afterPlaceOrderErrResp, afterPlaceOrderErrorCount }
        });

        blockingCalloutService.error(
            'Your order was successful, but there was a problem emailing your receipt. Please click retry or contact the restaurant.',
            undefined,
            {
                okText: 'Retry',
                okCallback: () => {
                    googleAnalyticsTrackingService.trackAfterPlaceOrderErrorRetry();
                    retryFunction();
                },
                cancelText: 'Got It',
                cancelCallback: () => {
                    googleAnalyticsTrackingService.trackAfterPlaceOrderErrorCancel();
                }
            }
        );

        // At this point, placeOrder was already successful so it's in the system and the
        // cart was deleted. If we reject the UI will still show the old cart.
        return $q.resolve();
    }

    function _setOrderData(dataFromResponse) {
        // Make sure we can access the final order data from checkoutComplete page. This will get
        // copied to the previous cart when cartService.clearCart() is eventually called.
        cartService.data.cart.shadowCart = dataFromResponse;

        const resolveOrderCompleteFn = () => {
            orderComplete.resolve({
                orderGuid: dataFromResponse.order.guid,
                openedDate: dataFromResponse.order.openedDate
            });
        };

        cartService.clearCartGuidAsync().then(() => {
            resolveOrderCompleteFn();
        });
    }

    function _trackEcommerceEvent(response, cart) {
        const check = cart.check;
        const checkId = response.data.checkGuid || response.data.order.checks[0].guid;

        for (const item of cart.items) {
            Analytics.addProduct(item.idString, item.name, undefined, undefined, undefined, item.price / item.quantity, item.quantity, undefined, undefined);
        }
        Analytics.trackTransaction(checkId, undefined, check.totalAmount, check.tax, check.deliveryFee, undefined, undefined, undefined, cart.diningOptionBehavior);
        Analytics.trackEvent('ecommerce', 'purchase', { nonInteraction: true });
    }

    var orderInfoValid = function() {
        var customer = getCustomer();
        var delivery = getDeliveryInfo();
        var payment = getPayment();
        var customerKeys = ['firstName', 'lastName', 'phone', 'email'];
        var deliveryKeys = ['latitude', 'longitude'];

        var customerValid = _objectValid(customer, customerKeys);
        var deliveryValid = _objectValid(delivery, deliveryKeys) || cartData.cart && cartData.cart.diningOptionBehavior === 'TAKE_OUT';
        var paymentValid = _paymentValid(payment);

        return customerValid && deliveryValid && paymentValid;
    };

    function _paymentValid(payment) {
        if(!payment.method) return false;
        if(payment.method !== 'CREDIT') return true;
        if(payment.guid) return true;

        const paymentKeys = ['cardNumber', 'expiryMonth', 'expiryYear', 'postalCode', 'securityCode'];
        return _objectValid(payment, paymentKeys);
    }

    function _objectValid(object, requiredKeys) {
        var toReturn = true;
        for (var i = 0; i < requiredKeys.length; i++) {
            if (object[requiredKeys[i]] === undefined ||
                    object[requiredKeys[i]] === null ||
                    object[requiredKeys[i]] === '') {
                toReturn = false;
            }
        }
        return toReturn;
    }

    function transformAddress(addr) {
        let address = angular.copy(addr);
        address.address1 = address.streetAddress1;
        address.address2 = address.streetAddress2;
        return address;
    }

    function _placeOrderUrl(cartGuid, customerGuid) {
        let url = 'carts/';

        url += 'enlightened/';

        url += `${cartGuid}/orders`;

        if (customerGuid) {
            url = `customers/${customerGuid}/${url}`;
        }

        return _onlineOrderingServiceUrl(url);
    }

    function _onlineOrderingServiceUrl(url) {
        return `${ToastApiService.data.url}/online-ordering/v1/${url}`;
    }


    activate();

    return {
        clearCCData: clearCCData,
        clearOrderData: clearOrderData,
        clearAllData: clearAllData,
        setCustomerData: setCustomerData,
        setCustomerDatum: setCustomerDatum,
        setDeliveryInfoData: setDeliveryInfoData,
        setDeliveryInfoDatum: setDeliveryInfoDatum,
        setPaymentData: setPaymentData,
        clearTip: clearTip,
        getCustomer: getCustomer,
        getDeliveryInfo: getDeliveryInfo,
        getPayment: getPayment,
        placeOrder: placeOrder,
        orderInfoValid: orderInfoValid,
        getOrderData,
        transformAddress,

        _placeOrderErrorHandlerCalloutConfig // for unit tests
    };
}
